import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Select, MenuItem, Stack, Box, Typography, TextField
} from '@mui/material';

const AddTimeoffDialog = ({
    open,
    onClose,
    onAdd,
    timeOff,
    selectedTimeOff,
    onSelectedTimeOffChange,
    onOpenAddTimeoffModal,
    relationshipName,
}) => {
    const partnerFirstName = relationshipName?.split(' ')[0] || 'Partner';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' } }}
        >
            <DialogTitle sx={{ fontWeight: 600, color: '#333', borderBottom: '1px solid #E8E8E8', pb: 2 }}>
                Add Time Off
            </DialogTitle>
            <DialogContent sx={{ pt: 3, pb: 2 }}>
                <Stack spacing={3}>
                    <Box sx={{ mt: 1 }}>
                        <Select
                            fullWidth
                            value={selectedTimeOff.timeoff_id || ''}
                            onChange={(e) => {
                                if (e.target.value === 'add_new') {
                                    onOpenAddTimeoffModal();
                                    return;
                                }
                                onSelectedTimeOffChange({ ...selectedTimeOff, timeoff_id: e.target.value });
                            }}
                            displayEmpty
                            sx={{ borderRadius: 2, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E8E8E8' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7B6B8D' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7B6B8D' } }}
                        >
                            <MenuItem value="" disabled sx={{ color: '#999' }}>Select Time Off</MenuItem>
                            {timeOff.map((time) => (
                                <MenuItem key={time.timeoff_id} value={time.timeoff_id}>{time.name}</MenuItem>
                            ))}
                            {timeOff.length > 0 && (
                                <MenuItem disabled sx={{ borderTop: '1px solid #E8E8E8', margin: '4px 0', py: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: '#999' }}>───────────</Typography>
                                </MenuItem>
                            )}
                            <MenuItem value="add_new" sx={{ fontStyle: 'italic', color: '#7B6B8D', fontWeight: 600 }}>
                                + Add New Time Off Type
                            </MenuItem>
                        </Select>
                    </Box>

                    {selectedTimeOff.timeoff_id && (
                        <Box>
                            <Typography variant="body2" sx={{ color: '#666', display: 'block', mb: 1.5, fontWeight: 600 }}>
                                Duration (Hours)
                            </Typography>
                            <Select
                                fullWidth
                                value={selectedTimeOff.hours || ''}
                                onChange={(e) => onSelectedTimeOffChange({ ...selectedTimeOff, hours: e.target.value })}
                                displayEmpty
                                sx={{ borderRadius: 2, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E8E8E8' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7B6B8D' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7B6B8D' } }}
                            >
                                <MenuItem value="" disabled sx={{ color: '#999' }}>Select Hours</MenuItem>
                                {[...Array(24).keys()].map((num) => (
                                    <MenuItem key={`add-hours-${num + 1}`} value={num + 1}>
                                        {num + 1} {num + 1 === 1 ? 'hour' : 'hours'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    )}

                    {selectedTimeOff.timeoff_id && (
                        <Box>
                            <Typography variant="body2" sx={{ color: '#666', display: 'block', mb: 1.5, fontWeight: 600 }}>
                                Who took time off?
                            </Typography>
                            <Stack direction="row" spacing={2}>
                                <Button
                                    fullWidth
                                    variant={selectedTimeOff.value === '0' ? 'contained' : 'outlined'}
                                    onClick={() => onSelectedTimeOffChange({ ...selectedTimeOff, value: '0' })}
                                    sx={{
                                        borderRadius: 2, py: 1.5, borderColor: '#7B6B8D',
                                        color: selectedTimeOff.value === '0' ? 'white' : '#7B6B8D',
                                        background: selectedTimeOff.value === '0' ? '#7B6B8D' : 'transparent',
                                        '&:hover': { borderColor: '#6A5A7A', background: selectedTimeOff.value === '0' ? '#6A5A7A' : 'rgba(184, 167, 212, 0.08)' }
                                    }}
                                >
                                    Me
                                </Button>
                                <Button
                                    fullWidth
                                    variant={selectedTimeOff.value === '1' ? 'contained' : 'outlined'}
                                    onClick={() => onSelectedTimeOffChange({ ...selectedTimeOff, value: '1' })}
                                    sx={{
                                        borderRadius: 2, py: 1.5, borderColor: '#C4856A',
                                        color: selectedTimeOff.value === '1' ? 'white' : '#C4856A',
                                        background: selectedTimeOff.value === '1' ? '#C4856A' : 'transparent',
                                        '&:hover': { borderColor: '#B0745A', background: selectedTimeOff.value === '1' ? '#B0745A' : 'rgba(244, 181, 196, 0.08)' }
                                    }}
                                >
                                    {partnerFirstName}
                                </Button>
                            </Stack>
                        </Box>
                    )}

                    {selectedTimeOff.timeoff_id && (
                        <Box>
                            <Typography variant="body2" sx={{ color: '#666', display: 'block', mb: 1.5, fontWeight: 600 }}>
                                Comments (Optional)
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Add any additional notes..."
                                value={selectedTimeOff.comments || ''}
                                onChange={(e) => onSelectedTimeOffChange({ ...selectedTimeOff, comments: e.target.value })}
                                inputProps={{ maxLength: 200 }}
                                helperText={`${(selectedTimeOff.comments || '').length}/200`}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&.Mui-focused fieldset': { borderColor: '#7B6B8D' } } }}
                            />
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #E8E8E8' }}>
                <Button
                    onClick={onClose}
                    sx={{ borderRadius: 2, px: 3, color: '#666', '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onAdd}
                    disabled={!selectedTimeOff.timeoff_id || !selectedTimeOff.hours || parseInt(selectedTimeOff.hours) <= 0 || !selectedTimeOff.value}
                    variant="contained"
                    sx={{
                        borderRadius: 2, px: 3, background: '#7B6B8D', boxShadow: 'none',
                        '&:hover': { background: '#6A5A7A', boxShadow: 'none' },
                        '&:disabled': { background: '#E8E8E8', color: '#999' }
                    }}
                >
                    Add Time Off
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddTimeoffDialog;
